/**
 * Preparo e envio de mídia do agendador, no navegador.
 *
 * - Imagem vira JPEG aqui (canvas): o Instagram só aceita JPEG, e converter no
 *   navegador poupa uma dependência nativa no servidor. Foto grande é reduzida
 *   para 1440 px no lado maior — o máximo que o Instagram exibe — o que também
 *   mantém o arquivo abaixo dos 8 MB.
 * - Vídeo vai como está; só lemos duração e dimensões para validar antes.
 * - O envio é direto para o bucket (URL assinada pelo servidor), com progresso.
 */

import type { MediaKind } from "@shared/social-publishing";

export type MidiaPronta = {
  /** Id local, para a lista da tela. */
  key: string;
  kind: MediaKind;
  mimeType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  /** Para a prévia, antes e depois do envio. */
  previewUrl: string;
  /** Caminho no bucket, quando o envio terminou. */
  path: string | null;
  progresso: number;
  erro: string | null;
  arquivo: Blob | null;
};

const LADO_MAXIMO = 1440;

function carregarImagem(arquivo: Blob): Promise<HTMLImageElement> {
  return new Promise((ok, falha) => {
    const img = new Image();
    img.onload = () => ok(img);
    img.onerror = () => falha(new Error("Não consegui abrir esta imagem."));
    img.src = URL.createObjectURL(arquivo);
  });
}

async function paraJpeg(arquivo: File): Promise<{ blob: Blob; width: number; height: number }> {
  const img = await carregarImagem(arquivo);
  const escala = Math.min(1, LADO_MAXIMO / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.round(img.naturalWidth * escala);
  const h = Math.round(img.naturalHeight * escala);
  // JPEG já no tamanho certo vai como está: reconverter só perde qualidade.
  if (arquivo.type === "image/jpeg" && escala === 1 && arquivo.size <= 8 * 1024 * 1024) {
    return { blob: arquivo, width: w, height: h };
  }
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("O navegador não conseguiu converter a imagem.");
  // Fundo branco: PNG transparente viraria preto no JPEG.
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, "image/jpeg", 0.9));
  if (!blob) throw new Error("O navegador não conseguiu converter a imagem.");
  return { blob, width: w, height: h };
}

function lerVideo(arquivo: File): Promise<{ durationMs: number; width: number; height: number }> {
  return new Promise((ok, falha) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      ok({ durationMs: Math.round(v.duration * 1000), width: v.videoWidth, height: v.videoHeight });
      URL.revokeObjectURL(v.src);
    };
    v.onerror = () => falha(new Error("Não consegui ler este vídeo. Use MP4 (H.264)."));
    v.src = URL.createObjectURL(arquivo);
  });
}

/** Lê o arquivo e devolve a mídia pronta para enviar (ainda sem `path`). */
export async function prepararArquivo(arquivo: File): Promise<MidiaPronta> {
  const key = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  if (arquivo.type.startsWith("image/")) {
    const { blob, width, height } = await paraJpeg(arquivo);
    return {
      key,
      kind: "image",
      mimeType: "image/jpeg",
      sizeBytes: blob.size,
      width,
      height,
      durationMs: null,
      previewUrl: URL.createObjectURL(blob),
      path: null,
      progresso: 0,
      erro: null,
      arquivo: blob,
    };
  }
  if (arquivo.type === "video/mp4" || arquivo.type === "video/quicktime") {
    const meta = await lerVideo(arquivo);
    return {
      key,
      kind: "video",
      mimeType: arquivo.type,
      sizeBytes: arquivo.size,
      width: meta.width || null,
      height: meta.height || null,
      durationMs: meta.durationMs,
      previewUrl: URL.createObjectURL(arquivo),
      path: null,
      progresso: 0,
      erro: null,
      arquivo,
    };
  }
  throw new Error(`"${arquivo.name}" não é imagem nem vídeo MP4/MOV.`);
}

/** PUT direto no bucket, com progresso. XHR porque `fetch` não informa progresso de envio. */
export function enviarParaBucket(
  uploadUrl: string,
  arquivo: Blob,
  mimeType: string,
  onProgresso: (pct: number) => void,
): Promise<void> {
  return new Promise((ok, falha) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", mimeType);
    xhr.setRequestHeader("x-upsert", "false");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgresso(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? ok()
        : falha(new Error(`O envio do arquivo falhou (status ${xhr.status}).`));
    xhr.onerror = () => falha(new Error("A conexão caiu durante o envio do arquivo."));
    xhr.send(arquivo);
  });
}
